import { ButtonTertiary } from "@nova/component-btn"
import * as Icons from "@nova/component-icons"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { Select } from "@nova/component-select"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useState } from "react"
import { useParams } from "react-router"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useConfirmationModalContext } from "features/modals/WorkspaceConfirmationModalV2"
import { Card } from "features/pilot-tracking/MetricLayout"
import { useCustomFieldDefinition } from "./CustomFieldsQueries"
import {
    useMissionCustomFields,
    useUpsertMissionCustomFieldAnswer,
} from "./MissionCustomFieldsQueries"
import { CustomFieldBuilder } from "./CustomFieldBuilder"
import { CustomFieldAnswer, MissionCustomFieldDefinition, CustomFieldSpec } from "@nova/schemas"

export const CUSTOM_FIELDS_SECTION_ID = "custom-fields-section"

const useCustomFieldsBuilderModal = () => {
    const modal = useConfirmationModalContext()

    return (initialForm: CustomFieldSpec[]) =>
        modal.confirm({
            RenderComponent: ({ confirm, cancel }) => (
                <HighPriorityNovaModalContainer
                    hideTitleUnderline
                    title="Flight Custom Fields"
                    closeModal={cancel}
                    className="w-[600px]"
                >
                    <div className="mt-4">
                        <CustomFieldBuilder
                            initialForm={initialForm}
                            onSaved={confirm}
                            onCancel={cancel}
                        />
                    </div>
                </HighPriorityNovaModalContainer>
            ),
        })
}

export const CustomFieldsCard = () => {
    const { isAdmin } = useWorkspacePermissions()
    const { missionId } = useParams<{ missionId: string }>()

    const { data: customFields } = useCustomFieldDefinition()
    const { data: missionAnswers } = useMissionCustomFields({ missionId: missionId ?? "" })

    if (!customFields?.form.length && isAdmin) {
        return <EmptyCustomFields />
    }

    if (!customFields || !missionId) return null

    return (
        <CustomFieldsDisplay
            missionId={missionId}
            answers={missionAnswers?.answers ?? []}
            customFields={customFields}
        />
    )
}

const EmptyCustomFields = () => {
    const openBuilderModal = useCustomFieldsBuilderModal()

    return (
        <Card>
            <div className="flex flex-1 items-center justify-between">
                <div className="flex items-center gap-3">
                    <h2 className="text-white telegraf">Custom Fields</h2>
                    <h5 className="text-gray-400">
                        Add questions to flights for your team to answer.
                    </h5>
                </div>
                <ButtonTertiary
                    title="Create Template"
                    LeftIcon={Icons.Plus}
                    onClick={() => openBuilderModal([])}
                />
            </div>
        </Card>
    )
}

const CustomFieldsDisplay = ({
    customFields,
    answers,
    missionId,
}: {
    customFields: MissionCustomFieldDefinition
    answers: CustomFieldAnswer[]
    missionId: string
}) => {
    const { isAdmin } = useWorkspacePermissions()
    const openBuilderModal = useCustomFieldsBuilderModal()
    const { upsertMissionCustomFieldAnswer } = useUpsertMissionCustomFieldAnswer({ missionId })

    const answersById = new Map(answers.map((a) => [a.id, a]))

    return (
        <div id={CUSTOM_FIELDS_SECTION_ID} className="flex flex-col gap-3">
            <div className="flex items-center justify-between">
                <h2 className="text-gray-50 telegraf">Custom Fields</h2>
                {isAdmin && (
                    <ButtonTertiary
                        title="Manage Custom Fields"
                        onClick={() => openBuilderModal(customFields.form)}
                    />
                )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 rounded-xl border border-white-100 overflow-hidden">
                {customFields.form.map((field) => (
                    <CustomField
                        key={field.id}
                        answer={answersById.get(field.id)}
                        question={field}
                        onSave={(value) =>
                            upsertMissionCustomFieldAnswer({ questionId: field.id, value })
                        }
                    />
                ))}
            </div>
        </div>
    )
}

const CustomField = ({
    question,
    answer,
    onSave,
}: {
    question: CustomFieldSpec
    answer: CustomFieldAnswer | undefined
    onSave: (value: CustomFieldAnswer["value"]) => void
}) => {
    const savedValue = answer?.value ?? ""
    const [draft, setDraft] = useState<CustomFieldAnswer["value"]>(savedValue)
    const [isEditing, setIsEditing] = useState(false)

    const startEditing = () => {
        setDraft(savedValue)
        setIsEditing(true)
    }

    const commit = (next: CustomFieldAnswer["value"] = draft) => {
        setIsEditing(false)
        if (next !== savedValue) onSave(next)
    }

    return (
        <div className="flex items-center px-4 py-3 gap-4 border-b border-r border-white-100 -mb-px -mr-px w-full">
            <div className="flex-1 text-gray-300 font-medium">{question.label}</div>
            <div className="flex-1 h-10 content-center">
                {isEditing ? (
                    <ClickAwayListener portalId="portal" onClickAway={() => commit()}>
                        <CustomFieldInput
                            question={question}
                            value={draft}
                            onChange={setDraft}
                            onCommit={commit}
                        />
                    </ClickAwayListener>
                ) : (
                    <div
                        className="cursor-pointer min-h-[2rem] flex items-center px-4 py-2 rounded-md text-gray-0 hover:bg-white-100"
                        onClick={startEditing}
                    >
                        {savedValue !== "" && savedValue !== undefined
                            ? formatAnswerValue(savedValue)
                            : "-"}
                    </div>
                )}
            </div>
        </div>
    )
}

const formatAnswerValue = (value: CustomFieldAnswer["value"]) => {
    if (typeof value === "boolean") return value ? "Yes" : "No"
    return value
}

const CustomFieldInput = ({
    question,
    value,
    onChange,
    onCommit,
}: {
    question: CustomFieldSpec
    value: CustomFieldAnswer["value"]
    onChange: (value: CustomFieldAnswer["value"]) => void
    onCommit: (value: CustomFieldAnswer["value"]) => void
}) => {
    const buttonClassName = "bg-white-100 border-transparent rounded-md"

    switch (question.type) {
        case "text":
            return (
                <input
                    autoFocus
                    className="w-full bg-white-100 rounded-md px-4 py-2 text-[14px] leading-[140%] text-gray-0 placeholder-gray-300 border-0 outline-none focus:outline-none focus:ring-1 focus:ring-blue-200"
                    value={typeof value === "string" ? value : ""}
                    onChange={(e) => onChange(e.target.value)}
                />
            )
        case "yes-no":
            return (
                <Select<string | undefined>
                    defaultOpen
                    buttonClassName={buttonClassName}
                    value={value === true ? "yes" : value === false ? "no" : undefined}
                    options={[
                        { label: "Yes", value: "yes" },
                        { label: "No", value: "no" },
                    ]}
                    onChange={(v) => onCommit(v === "yes")}
                />
            )
        case "multiple-choice":
            return (
                <Select<string | undefined>
                    defaultOpen
                    buttonClassName={buttonClassName}
                    value={typeof value === "string" ? value : undefined}
                    options={question.options.map((o) => ({ label: o, value: o }))}
                    onChange={(v) => v !== undefined && onCommit(v)}
                />
            )
    }
}
